import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { 
  Save,
  Download,
  Upload,
  Trash2,
  CheckCircle,
  XCircle,
  Loader2,
  AlertTriangle,
  FileJson
} from 'lucide-react';

function CustomConfigManager() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  
  // Form states
  const [templateName, setTemplateName] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');
  const [configText, setConfigText] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState(null);

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/config-templates');
      if (response.data.success) {
        setTemplates(response.data.templates || []);
      }
    } catch (err) {
      console.error('Failed to load templates:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setConfigText(e.target.result);
      };
      reader.readAsText(file);
    }
  };

  const handleSaveTemplate = async () => {
    if (!templateName.trim()) {
      setSaveError('Template name is required');
      return;
    }

    if (!configText.trim()) {
      setSaveError('Configuration content is required');
      return;
    }

    setSaving(true);
    setSaveError(null);
    setSaveSuccess(false);

    try {
      // Try to parse as JSON to validate
      let configToSave;
      try {
        configToSave = JSON.parse(configText);
      } catch (parseErr) {
        setSaveError('Invalid JSON format');
        setSaving(false);
        return;
      }

      const templateData = {
        name: templateName,
        description: templateDescription,
        config: configToSave
      };

      const response = await axios.post('/api/config-templates', templateData);
      
      if (response.data.success) {
        setSaveSuccess(true);
        setTemplateName('');
        setTemplateDescription('');
        setConfigText('');
        await loadTemplates();
        setTimeout(() => {
          setShowCreateForm(false);
          setSaveSuccess(false);
        }, 1500);
      }
    } catch (err) {
      setSaveError(err.response?.data?.error || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;
    
    try {
      await axios.delete(`/api/config-templates/${templateId}`);
      await loadTemplates();
    } catch (err) {
      console.error('Failed to delete template:', err);
    }
  };

  const handleDownloadTemplate = (template) => {
    const dataStr = JSON.stringify(template.config, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${template.name}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileJson className="w-5 h-5 text-purple-600" />
          Custom Configuration Files
        </CardTitle>
        <CardDescription>
          Create and manage custom MDVR configuration files
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Create Button */}
        {!showCreateForm && (
          <Button
            onClick={() => setShowCreateForm(true)}
            className="bg-purple-600 hover:bg-purple-700"
          >
            <Upload className="w-4 h-4 mr-2" />
            Create New Config File
          </Button>
        )}

        {/* Create Form */}
        {showCreateForm && (
          <Card className="border-2 border-purple-200 bg-purple-50">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Save className="w-5 h-5 text-purple-600" />
                  New Configuration File
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setShowCreateForm(false);
                    setSaveError(null);
                    setSaveSuccess(false);
                    setTemplateName('');
                    setTemplateDescription('');
                    setConfigText('');
                  }}
                >
                  <XCircle className="w-4 h-4" />
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Configuration Name *</Label>
                <Input
                  placeholder="e.g., Standard Fleet Config"
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Description (Optional)</Label>
                <Input
                  placeholder="Brief description of this configuration"
                  value={templateDescription}
                  onChange={(e) => setTemplateDescription(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label>Upload Configuration File</Label>
                <Input
                  type="file"
                  accept=".json,.txt"
                  onChange={handleFileUpload}
                  className="cursor-pointer"
                />
                <p className="text-xs text-gray-500">
                  Upload a JSON file containing your custom configuration
                </p>
              </div>

              <div className="space-y-2">
                <Label>Or Paste Configuration JSON</Label>
                <textarea
                  value={configText}
                  onChange={(e) => setConfigText(e.target.value)}
                  placeholder='{"MDVR": { ... }}'
                  className="w-full p-3 border rounded font-mono text-xs min-h-[300px]"
                />
                <p className="text-xs text-gray-500">
                  Paste or edit your custom configuration in JSON format
                </p>
              </div>

              {saveError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5" />
                  <p className="text-sm text-red-600">{saveError}</p>
                </div>
              )}

              {saveSuccess && (
                <div className="p-3 bg-green-100 border border-green-300 rounded flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600 mt-0.5" />
                  <p className="text-sm text-green-700">Configuration saved successfully!</p>
                </div>
              )}

              <Button
                onClick={handleSaveTemplate}
                disabled={saving}
                className="w-full bg-purple-600 hover:bg-purple-700"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Configuration
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Templates List */}
        <div className="space-y-2">
          <h4 className="font-medium text-gray-700">Saved Configurations ({templates.length})</h4>
          {loading ? (
            <div className="flex items-center gap-2 p-4 bg-gray-50 rounded border">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span className="text-sm text-gray-500">Loading configurations...</span>
            </div>
          ) : templates.length === 0 ? (
            <div className="p-4 bg-gray-50 border rounded text-center text-gray-500">
              No configurations saved yet
            </div>
          ) : (
            <div className="space-y-2">
              {templates.map((template) => (
                <div
                  key={template.id}
                  className="p-3 bg-white border rounded-lg hover:border-purple-300 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <FileJson className="w-4 h-4 text-purple-600" />
                        <h4 className="font-medium text-gray-900">{template.name}</h4>
                      </div>
                      {template.description && (
                        <p className="text-xs text-gray-500 mt-1 ml-6">{template.description}</p>
                      )}
                      <div className="flex items-center gap-3 mt-2 ml-6">
                        <p className="text-xs text-gray-400">
                          Created: {new Date(template.created_at).toLocaleDateString()}
                        </p>
                        <Badge variant="outline" className="text-xs">
                          {template.created_by}
                        </Badge>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDownloadTemplate(template)}
                        title="Download JSON"
                      >
                        <Download className="w-3 h-3" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDeleteTemplate(template.id)}
                        className="text-red-600 hover:bg-red-50"
                        title="Delete"
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default CustomConfigManager;
